/**
 * The one place a status becomes a colour, an icon and a word.
 *
 * Two rules make this a module rather than scattered conditionals. First, a status
 * colour is never rendered alone - every entry carries an icon and a label, because
 * two of the four light-mode status colours sit below 3:1 against the surface and the
 * icon-plus-label pairing is what keeps them legible. Second, the four statuses map
 * onto the four status tokens as a strict escalation ladder, so nothing else in the
 * UI has to decide whether "unreachable" is worse than "unhealthy".
 */

import {
  AlertTriangle,
  CheckCircle2,
  CircleHelp,
  PlugZap,
  ShieldAlert,
  XCircle,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { LatencyThresholds, ServiceStatus, SiteInfo, Status } from '../api/types'

export interface StatusPresentation {
  label: string
  icon: LucideIcon
  /** Tailwind token name - `text-good`, `bg-good/10`, and so on. */
  token: 'good' | 'warning' | 'serious' | 'critical' | 'ink-muted'
  /** How it reads in a sentence, for tooltips and the table view. */
  description: string
}

export const STATUS_PRESENTATION: Record<Status, StatusPresentation> = {
  healthy: {
    label: 'Healthy',
    icon: CheckCircle2,
    token: 'good',
    description: 'Every check passed.',
  },
  degraded: {
    label: 'Degraded',
    icon: AlertTriangle,
    token: 'warning',
    description: 'A non-critical check failed; the service is still serving.',
  },
  unhealthy: {
    label: 'Unhealthy',
    icon: ShieldAlert,
    token: 'serious',
    description: 'A critical dependency failed - the endpoint returned 503.',
  },
  unreachable: {
    label: 'Unreachable',
    icon: PlugZap,
    token: 'critical',
    description: 'No response at all: DNS, TLS, connection or timeout.',
  },
}

export const SERVICE_PRESENTATION: Record<ServiceStatus, StatusPresentation> = {
  up: { label: 'Up', icon: CheckCircle2, token: 'good', description: 'Check passed.' },
  down: {
    label: 'Down',
    icon: XCircle,
    token: 'critical',
    description: 'Check failed.',
  },
  unknown: {
    label: 'Unknown',
    icon: CircleHelp,
    token: 'ink-muted',
    description: 'The response did not say.',
  },
}

/** Worst-last, so `Math.max` over indices gives the status to headline. */
export const STATUS_ORDER: Status[] = [
  'healthy',
  'degraded',
  'unhealthy',
  'unreachable',
]

/**
 * The worst status in a set.
 *
 * A roll-up must be the worst rather than the average: a headline reading "healthy"
 * while one host is down is worse than no headline.
 */
export function worstStatus(statuses: Iterable<Status>): Status {
  let worst: Status = 'healthy'
  for (const status of statuses) {
    if (STATUS_ORDER.indexOf(status) > STATUS_ORDER.indexOf(worst)) worst = status
  }
  return worst
}

/** Text colour class for a status token. */
export function statusText(token: StatusPresentation['token']): string {
  return {
    good: 'text-good',
    warning: 'text-warning',
    serious: 'text-serious',
    critical: 'text-critical',
    'ink-muted': 'text-ink-muted',
  }[token]
}

/** Mark (fill) colour as a raw CSS value, for SVG marks. */
export function statusFill(token: StatusPresentation['token']): string {
  return `var(--color-${token === 'ink-muted' ? 'ink-muted' : token})`
}

/**
 * Which severity a latency reading falls into.
 *
 * The meter fill uses this, so a slow-but-up service reads as a problem starting -
 * the shape of an outage that a plain up/down check misses entirely.
 */
export type LatencyBand = 'fast' | 'warning' | 'serious' | 'critical'

export function latencyBand(
  ms: number | null,
  thresholds: LatencyThresholds,
): LatencyBand | null {
  if (ms === null || Number.isNaN(ms)) return null
  if (ms >= thresholds.critical) return 'critical'
  if (ms >= thresholds.serious) return 'serious'
  if (ms >= thresholds.warning) return 'warning'
  return 'fast'
}

/**
 * Which bands apply to one reading: the check's own, else its site's, else the default.
 *
 * "Slow" is not one number across a dashboard. A cache lookup at 400ms is an incident
 * and an SMTP round trip at 400ms is a good day, so a single global set has to be loose
 * enough for the slowest check on the slowest site - which leaves it unable to say
 * anything about the fastest. Three levels, each narrower than the last, fix that.
 *
 * The backend merges each level over the one above it at load, so every set arriving
 * here is complete and this is a lookup, not a merge. That is also why a check nobody
 * has configured needs no code: it misses the map and lands on its site's bands, and
 * giving it its own is an edit to `service_latency_thresholds_ms` in sites.json.
 */
export function resolveThresholds(
  fallback: LatencyThresholds,
  site?: SiteInfo,
  serviceName?: string,
): LatencyThresholds {
  if (!site) return fallback
  const own = serviceName
    ? site.service_latency_thresholds_ms?.[serviceName.trim().toLowerCase()]
    : undefined
  return own ?? site.latency_thresholds_ms ?? fallback
}

export function latencyFill(band: LatencyBand | null): string {
  switch (band) {
    case 'critical':
      return 'var(--color-critical)'
    case 'serious':
      return 'var(--color-serious)'
    case 'warning':
      return 'var(--color-warning)'
    default:
      // Fast is not a status - it is the ordinary case, so it wears the accent
      // rather than the success colour, which is reserved for a passing check.
      return 'var(--color-accent)'
  }
}

/** The four escalation lanes of the queue pipeline, in order, with their ramp step. */
export const QUEUE_LANES = [
  {
    key: 'queued' as const,
    label: 'Queued',
    fill: 'var(--color-lane-1)',
    description: 'Waiting on the primary work queue for a consumer to take them.',
  },
  {
    key: 'retrying' as const,
    label: 'Retrying',
    fill: 'var(--color-lane-2)',
    description: 'Failed once and waiting out a backoff before being returned.',
  },
  {
    key: 'cooling_off' as const,
    label: 'Cooling off',
    fill: 'var(--color-lane-3)',
    description: 'On a dead queue, held by its TTL before another round of retries.',
  },
  {
    key: 'parked' as const,
    label: 'Parked',
    fill: 'var(--color-lane-4)',
    description: 'On a repair queue - terminal, and stuck until a human looks.',
  },
]
